import React, { useState } from "react";
import SimpleBar from "./MetricsSimpleBar";
import PieChart from "./MetricsPieChart";

type CrossDataSeries<T> = {
  venueId: number;
  label: string;
  total?: number;
  data: T[];
};

export type PieChartData = {
  name: string;
  percentageValue: number;
  value: number;
};

type PropsBase = {
  sectionLabel: string;
  isCrossTab: boolean;
  columnData: { label: React.ReactNode; color?: string }[];
  hideClosedHeaders?: boolean;
};

type CrossTabProps = PropsBase & {
  isCrossTab: true;
  maxValue: number;
  crossData: CrossDataSeries<number>[];
};

type PieChartProps = PropsBase & {
  isCrossTab: false;
  maxValue?: never;
  crossData: CrossDataSeries<PieChartData[]>[];
};

export default function CrossDataTable(props: CrossTabProps | PieChartProps) {
  const { sectionLabel, crossData, hideClosedHeaders, isCrossTab, columnData } =
    props;

  const [isOpen, setIsOpen] = useState(true);

  const hasMultipleVenues = crossData.length > 1;

  crossData.sort(
    (
      d1: CrossDataSeries<number | PieChartData[]>,
      d2: CrossDataSeries<number | PieChartData[]>
    ) => d1.venueId - d2.venueId
  );

  return (
    <div className="metrics-box">
      <table className="metrics-table">
        <thead
          style={{ background: "#f4f4f4", borderBottom: 0 }}
          className="medium-text"
        >
          <tr>
            {/* the label for the whole box always shows */}
            <th
              style={{
                padding: "12px 16px",
              }}
            >
              {sectionLabel}
            </th>

            {/* labels for each column sometimes show */}
            {crossData.map(
              (s: CrossDataSeries<number | PieChartData[]>, i: number) => {
                const label =
                  isOpen && hasMultipleVenues // when open and there are multiple venues, show the label
                    ? s.label
                    : !isOpen && !hideClosedHeaders // when closed and hideClosedHeaders is false, show the total of each column
                    ? s.total
                    : ""; // otherwise don't show any column headers

                // on the last column, show an expand/collapse icon
                const expandCollapseIcon = i === crossData.length - 1 && (
                  <i className="material-icons">
                    {isOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                  </i>
                );

                return (
                  <th
                    key={i}
                    onClick={() => setIsOpen(!isOpen)}
                    style={{
                      fontSize: 14,
                      padding: "12px 16px",
                      position: "relative",
                    }}
                  >
                    {label}
                    <div
                      style={{
                        position: "absolute",
                        right: 8,
                        top: 10,
                        cursor: "pointer",
                      }}
                    >
                      {expandCollapseIcon}
                    </div>
                  </th>
                );
              }
            )}
          </tr>
        </thead>

        {/* only show the body if the table is open */}
        {isOpen && (
          <tbody>
            {
              // show data labels on the left (first column) and data on the right
              // one row per metric, one column per venue
              columnData.map(({ label, color }, i) => (
                <tr key={i} style={{ background: "white" }}>
                  <td style={{ borderBottom: 0 }}>{label}</td>

                  {/* display data chart per venue */}
                  {crossData.map(
                    (
                      venueData: CrossDataSeries<number | PieChartData[]>,
                      j: number
                    ) => (
                      <td
                        key={j}
                        style={{
                          borderLeft: "1px solid #d8d8dd",
                          borderBottom: 0,
                        }}
                      >
                        {i in venueData.data ? (
                          isCrossTab ? (
                            <SimpleBar
                              value={
                                (venueData as CrossDataSeries<number>).data[i]
                              }
                              maxValue={props.maxValue}
                              color={color || "#D8D8D8"}
                            />
                          ) : (
                            <PieChart
                              data={(venueData.data[i] as PieChartData[]).map(
                                (x) => ({
                                  name: x.name,
                                  percentageValue: x.percentageValue,
                                  value: x.value,
                                })
                              )}
                            />
                          )
                        ) : (
                          // show a dash if value is missing
                          "—"
                        )}
                      </td>
                    )
                  )}
                </tr>
              ))
            }
          </tbody>
        )}
      </table>
    </div>
  );
}
